<template>
  <div class="flex flex-wrap gap-2">
    <Button
      v-for="option in computedOptions"
      :key="option.label"
      :class="{'filter-btn-active': isActive(option.value)}"
      :variant="isActive(option.value) ? 'default' : 'outline'"
      @click="toggleValue(option.value)"
      size="sm"
      class="capitalize text-base font-medium"
      :disabled="option.value !== null && (!option.count || option.count === 0)"
      :aria-label="`${option.label}${option.count ? ' (' + option.count + ')' : ''}`"
    >
      {{ option.label }}
      <span v-if="option.value !== null && option.count && option.count > 0" class="ml-2 text-sm font-normal">
        ({{ option.count }})
      </span>
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Button } from '~/layers/base/components/ui/button';

interface Option {
  value: string | null;
  label: string;
  count?: number;
}

// Define props using withDefaults for potentially undefined activeValue
const props = withDefaults(defineProps<{
  filterKey: string;
  options: { value: string; label?: string; count?: number }[];
  activeValue?: string[] | null;
}>(), {
  activeValue: null
});

const emit = defineEmits<{
  'update:value': [value: string[] | null];
}>();

// Prepend "All" option and ensure labels/counts
const computedOptions = computed<Option[]>(() => [
  { value: null, label: 'All' },
  ...props.options.map(opt => ({ 
      value: opt.value, 
      label: opt.label || opt.value,
      count: opt.count
  })) 
]);

// Toggle logic remains the same
const toggleValue = (selectedValue: string | null) => {
  if (selectedValue === null) {
    emit('update:value', null);
  } else {
    // Ensure activeValue is treated as an array for manipulation
    const currentSelection = Array.isArray(props.activeValue) ? [...props.activeValue] : [];
    const index = currentSelection.indexOf(selectedValue);

    if (index === -1) {
      currentSelection.push(selectedValue); 
    } else {
      currentSelection.splice(index, 1);
    }
    emit('update:value', currentSelection.length > 0 ? currentSelection : null);
  }
};

// Active state logic remains the same
const isActive = (optionValue: string | null): boolean => {
  if (optionValue === null) {
    // "All" is active if activeValue is null, undefined, or empty array
    return !props.activeValue || props.activeValue.length === 0;
  }
  // Specific option is active if activeValue is an array containing it
  return Array.isArray(props.activeValue) && props.activeValue.includes(optionValue);
};
</script>

<style scoped>
/* Add any component-specific styles if needed */
.capitalize {
  text-transform: capitalize;
}

/* Style disabled buttons */
button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Tesla-like active button animation */
.filter-btn-active {
  transition: all 0.2s ease;
  transform: scale(1.02);
}
</style>
